import { useEffect, useRef, useState } from "react";
import { cachedGet } from "../lib/sharedData";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Check, Plus, Users, Crown, Shield, Eye, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const ROLE_ICON = { admin: Crown, agent: Shield, viewer: Eye };

export default function WorkspaceSwitcher() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  const refresh = () =>
    cachedGet("/workspaces/mine", 5 * 60000).then((data) => {
      setWorkspaces(data || []);
      const stored = localStorage.getItem("pm_active_workspace_id");
      const found = (data || []).find((w) => w.id === stored);
      const primary = (data || []).find((w) => w.is_primary) || (data || [])[0];
      setActiveId((found || primary)?.id || null);
    }).catch(() => {});

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const switchTo = (id) => {
    if (id === activeId) { setOpen(false); return; }
    try { localStorage.setItem("pm_active_workspace_id", id); } catch (e) { /* noop */ }
    setActiveId(id);
    setOpen(false);
    // Force a full reload so every page re-fetches scoped data with the new header
    window.location.reload();
  };

  const createWorkspace = async (e) => {
    e?.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const { data } = await api.post("/workspaces", { name });
      try { localStorage.setItem("pm_active_workspace_id", data.id); } catch (err) { /* noop */ }
      window.location.reload();
    } catch (err) {
      // surface inline; the modal stays open so the user can correct
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.detail || "Could not create workspace");
    } finally {
      setSaving(false);
    }
  };

  // Hide the switcher entirely until we have at least the default workspace loaded
  if (!workspaces.length) return null;

  const active = workspaces.find((w) => w.id === activeId) || workspaces[0];
  const ActiveIcon = ROLE_ICON[active?.my_role] || Shield;

  return (
    <div className="px-3 pt-1 pb-3 relative" ref={ref} data-testid="workspace-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10 text-left group"
        data-testid="workspace-switcher-btn"
        title={active?.name || ""}
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center font-display font-black text-sm shrink-0"
          style={{ background: "var(--pm-green)", color: "#062E17" }}
        >
          {(active?.name || "P").charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 leading-none mb-0.5">Workspace</div>
          <div className="text-sm font-bold text-white leading-tight line-clamp-2 break-words" data-testid="workspace-active-name">{active?.name}</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <ActiveIcon size={11} className="text-slate-400" />
          <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div
          className="absolute left-3 right-3 top-full mt-1 z-50 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
          data-testid="workspace-switcher-menu"
        >
          <div className="max-h-64 overflow-y-auto pm-scroll">
            <div className="px-3 py-2 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 border-b border-slate-100">Your workspaces</div>
            {workspaces.map((w) => {
              const Icon = ROLE_ICON[w.my_role] || Shield;
              const isActive = w.id === activeId;
              return (
                <button
                  key={w.id}
                  onClick={() => switchTo(w.id)}
                  data-testid={`workspace-option-${w.id}`}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 transition-colors text-left ${isActive ? "bg-emerald-50/40" : ""}`}
                >
                  <div className="w-7 h-7 rounded-md bg-emerald-50 text-emerald-800 flex items-center justify-center font-display font-black text-xs">
                    {w.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-900 truncate">{w.name}</div>
                    <div className="text-[11px] text-slate-500 capitalize flex items-center gap-1">
                      <Icon size={9} /> {w.my_role}
                    </div>
                  </div>
                  {isActive && <Check size={14} className="text-emerald-700 shrink-0" />}
                </button>
              );
            })}
          </div>
          <div className="border-t border-slate-100">
            {creating ? (
              <form onSubmit={createWorkspace} className="p-2 space-y-2 bg-slate-50">
                <input
                  type="text"
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Workspace name"
                  className="pm-input h-9 w-full text-sm"
                  data-testid="new-workspace-name-input"
                />
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => { setCreating(false); setNewName(""); }}
                    className="pm-btn-secondary h-8 px-3 rounded-lg text-[11px] font-bold flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving || !newName.trim()}
                    className="pm-btn-primary h-8 px-3 rounded-lg text-[11px] font-bold flex-1 flex items-center justify-center gap-1 disabled:opacity-50"
                    data-testid="create-workspace-submit"
                  >
                    {saving ? <Loader2 size={11} className="animate-spin" /> : null}
                    Create
                  </button>
                </div>
              </form>
            ) : (
              <>
                <button
                  onClick={() => setCreating(true)}
                  data-testid="create-workspace-btn"
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <Plus size={13} /> Create new workspace
                </button>
                <button
                  onClick={() => { setOpen(false); navigate("/settings/team"); }}
                  data-testid="manage-team-btn"
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-t border-slate-100"
                >
                  <Users size={13} /> Manage team & invitations
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
